/**
 * @module linkage/chartTypeSwitch/reconcileForChartType/reconcileDataLabel
 * @description 切图 L2 — 数据标签：过滤当前图不支持的 labelContent，开关开启时空数组补默认
 */

import { ensureLabelContentForChart } from '@dbl/core/chartEditor';
import type { TChartType } from '@dbl/core/chart';
import type { QBStateSnapshot } from '../../../types';
import { applyLayerPatch } from './applyLayerPatch';
import type { IReconcileLayerResult } from './reconcileTypes';

/**
 * 解析本层是否开启数据标签。
 * draft 常只含脏字段：本层未写 showDataLabel 时回落另一层，避免误跳过补默认。
 */
function resolveLayerShowDataLabel(
  layer: Record<string, unknown>,
  fallbackLayer: Record<string, unknown>,
): boolean {
  if (typeof layer.showDataLabel === 'boolean') {
    return layer.showDataLabel;
  }
  return fallbackLayer.showDataLabel === true;
}

function resolveLayerLabelContent(
  layer: Record<string, unknown>,
  fallbackLayer: Record<string, unknown>,
  chartType: TChartType,
  qb: QBStateSnapshot,
): { patch?: Record<string, unknown>; nextLayer: Record<string, unknown> } {
  // 脏 draft 常无此 key：仅清洗「拥有 labelContent」的层，避免把默认值物化进空 draft
  //（否则 pickFromMerged 优先 draft，会覆盖 base 中仍合法的多选项）
  if (!('labelContent' in layer)) {
    return { nextLayer: layer };
  }

  const showDataLabel = resolveLayerShowDataLabel(layer, fallbackLayer);
  const { next, changed } = ensureLabelContentForChart({
    labelContent: layer.labelContent,
    showDataLabel,
    chartType,
    dimensionCount: qb.columnDimensions?.length ?? 0,
    metricCount: qb.columnMetrics?.length ?? 0,
  });

  if (!changed) {
    return { nextLayer: layer };
  }

  const patch = { labelContent: next };
  return {
    patch,
    nextLayer: applyLayerPatch(layer, patch),
  };
}

/**
 * 切图后校正 labelContent：去掉目标图不支持的项；开关开启且为空时补默认。
 * 本层显式 `showDataLabel` 优先；缺省时回落另一层（适配 draft 脏字段模型）。
 * 仅 patch「拥有 labelContent」的层，避免空 draft 被物化默认值后覆盖 base 多选。
 */
export function reconcileDataLabelForChartType(
  chartType: TChartType,
  qb: QBStateSnapshot,
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): IReconcileLayerResult {
  const baseResult = resolveLayerLabelContent(base, draft, chartType, qb);
  const draftResult = resolveLayerLabelContent(draft, base, chartType, qb);

  return {
    base: baseResult.nextLayer,
    draft: draftResult.nextLayer,
    basePatch: baseResult.patch ?? {},
    draftPatch: draftResult.patch ?? {},
  };
}
